import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import { auth } from '../firebase';
import {
  Paper, Typography, Box, CircularProgress, Button, Link, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination,
  Dialog, DialogContent, DialogActions
} from '@mui/material';
import TableSortLabel from '@mui/material/TableSortLabel';
import { Link as RouterLink } from 'react-router-dom';
import DownloadIcon from '@mui/icons-material/Download';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Tooltip from '@mui/material/Tooltip';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import ErrorBlock from './common/ErrorBlock';

// Helper to format a date string to mm/dd/yy
const formatBirthDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    // Adjust for potential timezone issues by working with UTC dates
    const year = date.getUTCFullYear().toString().slice(-2);
    const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
    const day = date.getUTCDate().toString().padStart(2, '0');
    return `${month}/${day}/${year}`;
  } catch (e) {
    return 'Invalid Date';
  }
};

// Helper to format a datetime string to mm/dd/yy HH:MM:SS (24-hour)
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const datePart = formatBirthDate(dateString);
    const timePart = date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
    return `${datePart} ${timePart}`;
  } catch (e) {
    return 'Invalid Date';
  }
};

// Strip everything except 0–9
const digitsOnly = (s) => (s || '').toString().replace(/\D/g, '');


export default function UserReport() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem('userReportRowsPerPage');
    return saved ? Number(saved) : 25;
  });
  const [imageLoading, setImageLoading] = useState(null); // Track loading state by user ID
  const [search, setSearch] = useState('');
  
  // Sorting state for table headers
  const [orderBy, setOrderBy] = useState('created_at'); // default sort by Created At
  const [order, setOrder] = useState('desc');           // default direction

  // State for image modal viewer
  const [profileImageUrls, setProfileImageUrls] = useState([]);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [viewingUser, setViewingUser] = useState(null);

  const fetchUsers = () => {
    setLoading(true);
    api.get('/all-users')
      .then(res => {
        setUsers(res.data);
        setError(null);
      })
      .catch(err => {
        console.error('Error fetching user report:', err);
        setError('Failed to load user data. Please try again later.');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDownloadCsv = async () => {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Authentication required.');

      const token = await user.getIdToken();
      const url = `${api.defaults.baseURL}/all-users?format=csv`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error(`Download failed: ${response.statusText}`);

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', 'bidsy_all_users.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

    } catch (err) {
      console.error('CSV download error:', err);
      setError('Could not download CSV file.');
    }
  };

  const handleViewImage = async (userId, username) => {
    setImageLoading(userId);
    try {
      const res = await api.get(`/secure-image-url/${userId}`);
      if (res.data.urls && res.data.urls.length > 0) {
        setProfileImageUrls(res.data.urls);
        setSelectedImageIndex(0);
        setViewingUser(username || `User #${userId}`);
      } else {
        setError('Could not retrieve image URLs.');
      }
    } catch (err) {
      console.error('Error fetching secure image URLs:', err);
      setError('Failed to get images. They may have been deleted.');
    } finally {
      setImageLoading(null);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    localStorage.setItem('userReportRowsPerPage', event.target.value);
  };
  
  // Normalize values for robust sorting across types
  const getSortValue = (u, key) => {
    switch (key) {
      case 'id': return Number(u.id) || 0;
      case 'username': return (u.username || '').toLowerCase();
      case 'referredBy': return (u.referredByUsername || '').toLowerCase(); // Organic => ''
      case 'phone_number': return (u.phone_number || '');
      case 'country': return (u.country || '').toLowerCase();
      case 'birth_date': return Date.parse(u.birth_date || '') || -Infinity;
      case 'created_at': return Date.parse(u.created_at || '') || -Infinity;
      case 'updated_at': return Date.parse(u.updated_at || '') || -Infinity;
      case 'onboarded': return Number(u.onboarded) ?? -Infinity;
      default: {
        const v = u[key];
        if (typeof v === 'number') return v;
        if (v === null || v === undefined) return '';
        return String(v).toLowerCase();
      }
    }
  };

  const handleSort = (key) => {
    setOrderBy((prevKey) => {
      if (prevKey === key) {
        setOrder((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setOrder('asc');
      return key;
    });
    setPage(0);
  };
  
  const filteredUsers = useMemo(() => {
    const raw = (search || '').trim();
    if (!raw) return users;

    // Forced ID search: allow "id:123" or "#123"
    const idToken = raw.match(/^(?:id:|#)\s*(\d+)$/i);
    if (idToken) {
      const targetId = idToken[1];
      return users.filter(u => String(u.id) === targetId);
    }

    const qText   = raw.toLowerCase();
    const qDigits = digitsOnly(raw);
    const tokens  = qText.split(/\s+/).filter(Boolean);
    const organicOnly = tokens.includes('is:organic');

    // Only activate phone matching if the query includes at least 4 digits
    const tryPhone = qDigits.length >= 4;

    return users.filter((u) => {
      const usernameMatch = (u.username || '').toLowerCase().includes(qText);
      const countryMatch  = (u.country  || '').toLowerCase().includes(qText);

      // Exact ID match when the whole query is numeric and equals u.id
      const idMatch = qDigits && qDigits === String(u.id);

      let phoneMatch = false;
      if (tryPhone) {
        const phoneDigits = digitsOnly(u.phone_number);
        if (phoneDigits) {
          // Flexible: match anywhere in the number; also cover common "last 4/7" searches.
          phoneMatch =
            phoneDigits.includes(qDigits) ||
            phoneDigits.endsWith(qDigits);
        }
      }

      // Special token: is:organic => users without a referrer
      if (organicOnly) {
        const hasRef = !!(u.referredBy ?? u.referred_by);
        return !hasRef;
      }

      return idMatch || usernameMatch || countryMatch || phoneMatch;
    });
  }, [users, search]);

  // Apply sorting BEFORE pagination
  const sortedFilteredUsers = useMemo(() => {
    const arr = [...filteredUsers];
    arr.sort((a, b) => {
      const av = getSortValue(a, orderBy);
      const bv = getSortValue(b, orderBy);
      // numeric compare when both are numbers
      if (typeof av === 'number' && typeof bv === 'number') {
        return order === 'asc' ? av - bv : bv - av;
      }
      // string/date fallback
      return order === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return arr;
  }, [filteredUsers, orderBy, order]);

  // Define columns to display and their formatters
  const columns = [
    { id: 'profileImage', label: 'Picture', minWidth: 50,  sortable: false },
    { id: 'id',          label: 'ID',      minWidth: 50,  sortable: true,  sortKey: 'id' },
    { id: 'username',    label: 'Username',minWidth: 150, sortable: true,  sortKey: 'username' },
    // Sort "Referred by" using ref username (or '' for Organic)
    { id: 'referredBy',  label: 'Referred by', minWidth: 160, sortable: true, sortKey: 'referredBy' },
    { id: 'phone_number',label: 'Phone',   minWidth: 140, sortable: true,  sortKey: 'phone_number' },
    { id: 'birth_date',  label: 'Birthdate',minWidth: 100, format: formatBirthDate, sortable: true, sortKey: 'birth_date' },
    { id: 'created_at',  label: 'Created At',minWidth: 170, format: formatDateTime, sortable: true, sortKey: 'created_at' },
    { id: 'updated_at',  label: 'Updated At',minWidth: 170, format: formatDateTime, sortable: true, sortKey: 'updated_at' },
    { id: 'short_bio',   label: 'Bio',     minWidth: 250, sortable: true,  sortKey: 'short_bio' },
    { id: 'country',     label: 'Country', minWidth: 100, sortable: true,  sortKey: 'country' },
  ];

  if (loading) return <LoadingBlock height={480} />;

  if (error) return <ErrorBlock message={error} onRetry={fetchUsers} />;

  return (
    <>
      <PageHeader
        title="All Users Report"
        actions={(
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField size="small" placeholder="Search username, ID (#123 or id:123), country, or phone" value={search} onChange={(e) => setSearch(e.target.value)} />
            <Button variant="contained" startIcon={<DownloadIcon />} onClick={handleDownloadCsv}>Download CSV</Button>
          </Box>
        )}
      />
      <Paper sx={{ p: 2, width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 600 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(col => {
                const key = col.sortKey || col.id;
                const isActive = col.sortable && orderBy === key;
                return (
                  <TableCell
                    key={col.id}
                    sx={{ fontWeight: 'bold', minWidth: col.minWidth }}
                    sortDirection={isActive ? order : false}
                  >
                    {col.sortable ? (
                      <TableSortLabel
                        active={isActive}
                        direction={isActive ? order : 'asc'}
                        onClick={() => handleSort(key)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFilteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map(user => (
              <TableRow key={user.id} hover role="checkbox" tabIndex={-1} sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}>
                {columns.map(col => {
                  const value = user[col.id];
                  // "Referred by" — clickable username OR clickable "Organic"
                  if (col.id === 'referredBy') {
                    const refId = user.referredBy ?? user.referred_by ?? null;
                    const refName = user.referredByUsername ?? null;
                    return (
                      <TableCell key={col.id} sx={{ fontSize: '0.8rem' }}>
                        {refId ? (
                          <Link
                            component={RouterLink}
                            to={`/user/${refId}`}
                            state={{ from: { label: 'User Report', path: '/user-report' } }}
                            underline="hover"
                            sx={{ fontWeight: 600 }}
                            aria-label={`Open referrer user page for ${refName || `User #${refId}`}`}
                          >
                            {refName || `User #${refId}`}
                          </Link>
                        ) : (
                          <Link
                            component="button"
                            underline="hover"
                            sx={{ fontWeight: 500, color: 'primary.main' }}
                            onClick={() => setSearch('is:organic')}
                            aria-label="Filter to organic signups"
                          >
                            Organic
                          </Link>
                        )}
                      </TableCell>
                    );
                  }
                  // Custom cell for the profile picture link
                  if (col.id === 'profileImage') {
                    return (
                      <TableCell key={col.id} align={col.align} sx={{ fontSize: '0.8rem' }}>
                        {value ? (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleViewImage(user.id, user.username)}
                            disabled={imageLoading === user.id}
                            startIcon={imageLoading === user.id ? <CircularProgress size={14} /> : <PhotoCameraIcon />}
                          >
                            View
                          </Button>
                        ) : (
                          'None'
                        )}
                      </TableCell>
                    );
                  }
                  // Make the username a router link to the user page WITH approved icon and onboarded label
                  if (col.id === 'username') {
                    // The dating-era approval flag is gone; an approved account is
                    // now an approved creator. This icon read the dead flag and
                    // had not shown for anyone since the pivot.
                    const isApproved = Number(user.is_approved_creator) === 1;
                    return (
                      <TableCell key={col.id} sx={{ fontSize: '0.8rem' }}>
                        <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                            <Link
                              component={RouterLink}
                              to={`/user/${user.id}`}
                              state={{ from: { label: 'User Report', path: '/user-report' } }}
                              underline="hover"
                              sx={{ fontWeight: 600 }}
                              aria-label={`Open user page for ${user.username || user.id}`}
                            >
                              {user.username || user.id}
                            </Link>
                            {isApproved && (
                              <Tooltip title="Approved creator" placement="right" arrow>
                                <CheckCircleIcon
                                  fontSize="small"
                                  sx={{ color: (t) => t.palette.success.main }}
                                  aria-label="Approved creator"
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Box sx={{ color: 'text.secondary' }}>
                            Onboarded: {typeof user.onboarded === 'number' ? user.onboarded : (user.onboarded ?? '—')}
                          </Box>
                        </Box>
                      </TableCell>
                    );
                  }
                  return (
                    <TableCell key={col.id} align={['id'].includes(col.id) ? 'right' : col.align} sx={{ fontSize: '0.8rem' }}>
                      {col.format ? col.format(value) : String(value === null || value === undefined ? '' : value)}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>

    {/* Profile Image Lightbox Modal */}
    <Dialog
      open={selectedImageIndex !== null}
      onClose={() => setSelectedImageIndex(null)}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(0, 0, 0, 0.9)',
          boxShadow: 24,
          maxHeight: '95vh'
        }
      }}
    >
      <DialogContent 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          p: 2,
          position: 'relative'
        }}
      >
        {selectedImageIndex !== null && profileImageUrls[selectedImageIndex] && (
          <Box
            component="img"
            src={profileImageUrls[selectedImageIndex]}
            alt={`${viewingUser || 'User'} profile ${selectedImageIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 2
            }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ bgcolor: 'rgba(0, 0, 0, 0.9)', justifyContent: 'space-between', px: 2 }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={() => setSelectedImageIndex(prev => prev > 0 ? prev - 1 : profileImageUrls.length - 1)}
            variant="outlined"
            disabled={profileImageUrls.length <= 1}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Previous
          </Button>
          <Button 
            onClick={() => setSelectedImageIndex(prev => prev < profileImageUrls.length - 1 ? prev + 1 : 0)}
            variant="outlined"
            disabled={profileImageUrls.length <= 1}
            sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}
          >
            Next
          </Button>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: 'white', mr: 2 }}>
            {selectedImageIndex !== null ? `${selectedImageIndex + 1} / ${profileImageUrls.length}` : ''}
          </Typography>
          <Button 
            onClick={() => setSelectedImageIndex(null)} 
            variant="contained"
            sx={{ bgcolor: 'white', color: 'black', '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' } }}
          >
            Close
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
    </>
  );
} 